import React, { useState } from 'react';
import { isSameDay } from 'date-fns';
import {
    Paper,
    Typography,
    Box,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Tooltip,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Block,
    Event,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

interface CalendarEvent {
    id: string;
    date: string;
    type: 'reservation' | 'blocked' | 'manual_shift';
    title: string;
    description?: string;
    clientName?: string;
    status?: string;
    paymentStatus?: string;
}

interface Reservation {
    id: string;
    clientId?: string;
    customerName: string;
    customerEmail: string;
    startDate: string;
    endDate: string;
    status: string;
    statusHistory?: any[];
    paymentStatus?: 'pending' | 'partial' | 'paid' | 'refunded';
    completionStatus?: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'no_show' | 'cancelled';
    rescheduleCount?: number;
    lastRescheduleDate?: string;
    notes?: string;
}

interface MonthlyCalendarSchedulerProps {
    events?: CalendarEvent[];
    reservations?: Reservation[];
    weeklyAvailability?: {
        lunes: any[];
        martes: any[];
        miercoles: any[];
        jueves: any[];
        viernes: any[];
        sabado: any[];
        domingo: any[];
    };
    allow247?: boolean;
    blockedDates?: Record<string, string>;
    onDateClick?: (date: Date) => void;
    onAddEvent?: (date: Date, event: Partial<CalendarEvent>) => void;
    onBlockDate?: (date: Date, reason: string) => void;
    onUpdateBlockedDates?: (blockedDates: Record<string, string>) => void;
    onReservationClick?: (reservation: Reservation) => void;
}

const CalendarGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 1,
    '& > *': {
        aspectRatio: '1',
        minHeight: 80,
    },
}));

const DayCell = styled(Paper)<{ isToday?: boolean; isCurrentMonth?: boolean; isAvailable?: boolean; isSelected?: boolean }>(
    ({ theme, isToday, isCurrentMonth, isAvailable, isSelected }) => ({
        padding: theme.spacing(1),
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: isSelected 
            ? `2px solid ${theme.palette.primary.main}` 
            : (isToday ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent'),
        backgroundColor: isCurrentMonth 
            ? (isAvailable === false 
                ? theme.palette.action.disabledBackground 
                : (isSelected ? theme.palette.primary.light : 'transparent'))
            : theme.palette.grey[100],
        opacity: isCurrentMonth ? 1 : 0.5,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            transform: 'scale(1.02)',
        },
        position: 'relative',
        overflow: 'hidden',
    })
);

const EventChip = styled(Chip)<{ eventtype?: string }>(({ theme, eventtype }) => ({
    height: 20,
    fontSize: '0.7rem',
    marginBottom: 2,
    cursor: eventtype === 'reservation' ? 'pointer' : 'default',
    '& .MuiChip-label': {
        padding: '0 4px',
    },
    '&:hover': {
        transform: eventtype === 'reservation' ? 'scale(1.05)' : 'none',
    },
}));

const MonthlyCalendarScheduler: React.FC<MonthlyCalendarSchedulerProps> = ({
    events = [],
    reservations = [],
    weeklyAvailability,
    allow247 = false,
    blockedDates: externalBlockedDates,
    onDateClick,
    onAddEvent,
    onBlockDate,
    onUpdateBlockedDates,
    onReservationClick,
}) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [blockDialogOpen, setBlockDialogOpen] = useState(false);
    const [internalBlockedDates, setInternalBlockedDates] = useState<Record<string, string>>({});
    const [newEvent, setNewEvent] = useState({
        title: '',
        description: '',
        type: 'manual_shift' as const,
    });
    const [blockReason, setBlockReason] = useState('');

    const blockedDates = externalBlockedDates || internalBlockedDates;

    const dayNamesMap: Record<number, string> = {
        0: 'domingo',
        1: 'lunes',
        2: 'martes',
        3: 'miercoles',
        4: 'jueves',
        5: 'viernes',
        6: 'sabado',
    };

    const isDayAvailable = (date: Date): boolean => {
        if (allow247) return true;
        
        const dateStr = date.toISOString().split('T')[0];
        if (blockedDates[dateStr]) return false;
        
        if (!weeklyAvailability) return true;
        
        const dayOfWeek = date.getDay();
        const dayName = dayNamesMap[dayOfWeek];
        const dayAvailability = weeklyAvailability[dayName as keyof typeof weeklyAvailability];
        
        return dayAvailability && dayAvailability.length > 0;
    };

    const toggleDateBlocked = (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        const newBlocked = { ...blockedDates };
        if (newBlocked[dateStr]) {
            delete newBlocked[dateStr];
        } else {
            newBlocked[dateStr] = 'Bloqueado manualmente';
        }
        
        if (onUpdateBlockedDates) {
            onUpdateBlockedDates(newBlocked);
        } else {
            setInternalBlockedDates(newBlocked);
        }
    };

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        for (let i = 0; i < startingDayOfWeek; i++) {
            const prevDate = new Date(year, month, -startingDayOfWeek + i + 1);
            days.push({ date: prevDate, isCurrentMonth: false });
        }

        for (let i = 1; i <= daysInMonth; i++) {
            const date = new Date(year, month, i);
            days.push({ date, isCurrentMonth: true });
        }

        const remainingCells = 42 - days.length;
        for (let i = 1; i <= remainingCells; i++) {
            const nextDate = new Date(year, month + 1, i);
            days.push({ date: nextDate, isCurrentMonth: false });
        }

        return days;
    };

    const getEventsForDate = (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        const dayEvents = events.filter(event => event.date === dateStr);

        // Add reservations for this date
        const dayReservations = reservations.filter(reservation => {
            const reservationDate = new Date(reservation.startDate).toISOString().split('T')[0];
            return reservationDate === dateStr;
        }).map(reservation => ({
            id: reservation.id,
            date: dateStr,
            type: 'reservation' as const,
            title: reservation.customerName,
            description: `${new Date(reservation.startDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} - ${new Date(reservation.endDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`,
            clientName: reservation.customerName,
            status: reservation.completionStatus || reservation.status,
            paymentStatus: reservation.paymentStatus,
        }));

        return [...dayEvents, ...dayReservations];
    };

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
        if (onDateClick) {
            onDateClick(date);
        }
    };

    const handleAddEvent = () => {
        setEventDialogOpen(true);
    };

    const handleBlockDate = () => {
        setBlockDialogOpen(true);
    };

    const handleSaveEvent = () => {
        if (selectedDate && onAddEvent) {
            onAddEvent(selectedDate, {
                ...newEvent,
                date: selectedDate.toISOString().split('T')[0],
            });
            setEventDialogOpen(false);
            setNewEvent({ title: '', description: '', type: 'manual_shift' });
        }
    };

    const handleSaveBlock = () => {
        if (selectedDate) {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const newBlocked = { 
                ...blockedDates, 
                [dateStr]: blockReason || 'Bloqueado manualmente' 
            };
            
            if (onUpdateBlockedDates) {
                onUpdateBlockedDates(newBlocked);
            } else {
                setInternalBlockedDates(newBlocked);
            }
            setBlockDialogOpen(false);
            setBlockReason('');
        }
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentDate(prev => {
            const year = prev.getFullYear();
            const month = prev.getMonth();
            let newMonth: number;
            
            if (direction === 'prev') {
                newMonth = month - 1;
                if (newMonth < 0) {
                    return new Date(year - 1, 11, 1);
                }
            } else {
                newMonth = month + 1;
                if (newMonth > 11) {
                    return new Date(year + 1, 0, 1);
                }
            }
            
            return new Date(year, newMonth, 1);
        });
    };

    const days = getDaysInMonth(currentDate);
    const today = new Date();
    const isToday = (date: Date) =>
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();

    return (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                    Calendario Mensual
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigateMonth('prev')}>
                        <ChevronLeft />
                    </IconButton>
                    <Typography variant="h6">
                        {currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                    </Typography>
                    <IconButton onClick={() => navigateMonth('next')}>
                        <ChevronRight />
                    </IconButton>
                </Box>
            </Box>

            {/* Days of week header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 1 }}>
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                    <Typography key={day} variant="subtitle2" align="center" fontWeight="bold">
                        {day}
                    </Typography>
                ))}
            </Box>

            {/* Calendar Grid */}
            <CalendarGrid>
                {days.map(({ date, isCurrentMonth }, index) => {
                    const dayEvents = getEventsForDate(date);
                    const hasEvents = dayEvents.length > 0;
                    const available = isDayAvailable(date);
                    const dateStr = date.toISOString().split('T')[0];
                    const isBlocked = !!blockedDates[dateStr];
                    const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;

                    return (
                        <DayCell
                            key={index}
                            isToday={isToday(date)}
                            isCurrentMonth={isCurrentMonth}
                            isAvailable={available}
                            isSelected={isSelected}
                            onClick={() => handleDateClick(date)}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: isToday(date) ? 'bold' : 'normal',
                                        color: isToday(date) ? 'primary.main' : 'text.primary',
                                    }}
                                >
                                    {date.getDate()}
                                </Typography>
                                {isCurrentMonth && (
                                    <Tooltip title={isBlocked ? 'Bloqueado' : (available ? 'Disponible' : 'No disponible')}>
                                        <Box
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!isToday(date) || isCurrentMonth) {
                                                    toggleDateBlocked(date);
                                                }
                                            }}
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: isBlocked ? 'error.main' : (available ? 'success.main' : 'grey.400'),
                                                cursor: 'pointer',
                                                '&:hover': { transform: 'scale(1.3)' },
                                                transition: 'transform 0.2s',
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>

                            {hasEvents && (
                                <Box sx={{ mt: 0.5 }}>
                                    {dayEvents.slice(0, 2).map((event, eventIndex) => {
                                        const getEventColor = () => {
                                            if (event.type === 'reservation') {
                                                if (event.status === 'completed') return 'success';
                                                if (event.status === 'no_show') return 'error';
                                                if (event.status === 'in_progress') return 'warning';
                                                if (event.paymentStatus === 'paid') return 'info';
                                                return 'primary';
                                            }
                                            if (event.type === 'blocked') return 'error';
                                            return 'success';
                                        };

                                        return (
                                            <Tooltip
                                                key={eventIndex}
                                                title={
                                                    event.type === 'reservation'
                                                        ? `${event.title} - ${event.description || ''} (${event.status || 'pendiente'})`
                                                        : `${event.title}${event.clientName ? ` - ${event.clientName}` : ''}`
                                                }
                                            >
                                                <EventChip
                                                    eventtype={event.type}
                                                    label={event.title.length > 8 ? `${event.title.substring(0, 8)}...` : event.title}
                                                    size="small"
                                                    color={getEventColor()}
                                                    variant="filled"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (event.type === 'reservation' && onReservationClick) {
                                                            // Find the original reservation
                                                            const reservation = reservations.find(r => r.id === event.id);
                                                            if (reservation) {
                                                                onReservationClick(reservation);
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Tooltip>
                                        );
                                    })}
                                    {dayEvents.length > 2 && (
                                        <Typography variant="caption" color="text.secondary">
                                            +{dayEvents.length - 2} más
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </DayCell>
                    );
                })}
            </CalendarGrid>

            {/* Action Buttons */}
            {selectedDate && (
                <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Block />}
                        onClick={handleBlockDate}
                        size="small"
                    >
                        Bloquear Fecha
                    </Button>
                </Box>
            )}

            {/* Add Event Dialog */}
            <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Agregar Turno Manual - {selectedDate?.toLocaleDateString('es-ES')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Título del Turno"
                        value={newEvent.title}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Descripción"
                        value={newEvent.description}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEventDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveEvent} variant="contained">
                        Agregar Turno
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Block Date Dialog */}
            <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Bloquear Fecha - {selectedDate?.toLocaleDateString('es-ES')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Razón del bloqueo"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        placeholder="Ej: Mantenimiento, feriado, etc."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBlockDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveBlock} variant="contained" color="error">
                        Bloquear Fecha
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default MonthlyCalendarScheduler;